import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiChevronDown, FiChevronRight, FiDownload, FiTag, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import AdminMessageTemplates from './AdminMessageTemplates';
import AdminBlast from './AdminBlast';

const authH = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const pBtn = (d) => ({ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, cursor: d ? 'default' : 'pointer', background: '#fff', color: d ? '#d1d5db' : '#374151', opacity: d ? 0.5 : 1 });

// ── Contacts Panel ─────────────────────────────────────────────────────────
function ContactsPanel({ locationId, token }) {
  const [data, setData]     = useState(null);
  const [page, setPage]     = useState(1);
  const [limit, setLimit]   = useState(25);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState({});

  const load = useCallback(async (p, l) => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/admin/ghl/subaccounts/${locationId}/contacts?page=${p}&limit=${l}`, authH(token));
      setData(r.data);
    } catch (e) { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  }, [locationId, token]);

  useEffect(() => { load(1, 25); }, [locationId]);
  const changePage  = (p) => { setPage(p); load(p, limit); };
  const changeLimit = (l) => { setLimit(l); setPage(1); load(1, l); };

  const downloadPdf = async (email) => {
    if (!email) return toast.warn('No email for this contact');
    setPdfLoading(p => ({ ...p, [email]: true }));
    try {
      const r = await axios.get(`/api/admin/ghl/subaccounts/${locationId}/contact-pdf?email=${encodeURIComponent(email)}`, authH(token));
      if (r.data?.pdfUrl) { const a = document.createElement('a'); a.href = r.data.pdfUrl; a.target = '_blank'; a.download = email + '.pdf'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }
      else toast.warn('No PDF found');
    } catch (e) { toast.error(e.response?.data?.error || 'PDF not available'); }
    finally { setPdfLoading(p => ({ ...p, [email]: false })); }
  };

  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit) || 1;
  const contacts = data?.contacts || [];
  const noToken = data?._note === 'no_token';

  if (noToken) return (
    <div style={{ padding: '16px 20px', background: '#fffbeb', borderTop: '1px solid #fde68a' }}>
      <span style={{ fontSize: 13, color: '#92400e' }}>
        ⚠️ Could not load contacts — OAuth token unavailable for this subaccount. Ensure the agency OAuth connection is active.
      </span>
    </div>
  );

  return (
    <div style={{ padding: '16px 20px', background: '#f8faff', borderTop: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: '#6b7280' }}>{loading ? 'Loading…' : `${total.toLocaleString()} contacts`}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: '#6b7280' }}>Per page:</label>
          <select value={limit} onChange={e => changeLimit(Number(e.target.value))} style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: 12 }}>
            {[10, 25, 50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <button onClick={() => load(page, limit)} style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer' }}>Refresh</button>
        </div>
      </div>
      {loading && contacts.length === 0 ? <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af' }}>Loading contacts…</div>
      : contacts.length === 0 ? <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af' }}>No contacts found</div>
      : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#eff6ff' }}>
              {['Name','Email','Phone','Tags','Created','PDF'].map(h=><th key={h} style={{ padding:'8px 12px',textAlign:'left',fontWeight:600,color:'#374151',borderBottom:'1px solid #dbeafe',whiteSpace:'nowrap'}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {contacts.map((c,i)=>(
                <tr key={c.id||i} style={{ background:i%2===0?'#fff':'#f9fafb',borderBottom:'1px solid #f3f4f6'}}>
                  <td style={{padding:'8px 12px',fontWeight:500}}>{[c.firstName,c.lastName].filter(Boolean).join(' ')||<span style={{color:'#9ca3af'}}>—</span>}</td>
                  <td style={{padding:'8px 12px',color:'#1a56db'}}>{c.email||'—'}</td>
                  <td style={{padding:'8px 12px'}}>{c.phone||'—'}</td>
                  <td style={{padding:'8px 12px'}}>{(c.tags||[]).length>0?c.tags.map(t=><span key={t} style={{display:'inline-block',background:'#eff6ff',color:'#1d4ed8',borderRadius:10,padding:'1px 8px',fontSize:11,marginRight:3}}>{t}</span>):<span style={{color:'#d1d5db',fontSize:11}}>none</span>}</td>
                  <td style={{padding:'8px 12px',color:'#6b7280',fontSize:12}}>{c.createdAt?new Date(c.createdAt).toLocaleDateString():'—'}</td>
                  <td style={{padding:'8px 12px'}}><button onClick={()=>downloadPdf(c.email)} disabled={!c.email||pdfLoading[c.email]} title={c.email?'Download PDF':'No email'} style={{background:'none',border:'none',cursor:c.email?'pointer':'not-allowed',color:c.email?'#1a56db':'#d1d5db',padding:4}}>{pdfLoading[c.email]?'…':<FiDownload size={15}/>}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && (
        <div style={{ display:'flex',alignItems:'center',gap:6,marginTop:12,flexWrap:'wrap' }}>
          <button onClick={()=>changePage(1)} disabled={page===1} style={pBtn(page===1)}>«</button>
          <button onClick={()=>changePage(page-1)} disabled={page===1} style={pBtn(page===1)}>‹</button>
          {Array.from({length:Math.min(totalPages,7)},(_,i)=>{const p2=page<=4?i+1:page-3+i;if(p2<1||p2>totalPages)return null;return<button key={p2} onClick={()=>changePage(p2)} style={{...pBtn(false),background:p2===page?'#1a56db':'#fff',color:p2===page?'#fff':'#374151',fontWeight:p2===page?700:400}}>{p2}</button>;})}
          <button onClick={()=>changePage(page+1)} disabled={page===totalPages} style={pBtn(page===totalPages)}>›</button>
          <button onClick={()=>changePage(totalPages)} disabled={page===totalPages} style={pBtn(page===totalPages)}>»</button>
          <span style={{fontSize:12,color:'#6b7280',marginLeft:4}}>Page {page} of {totalPages}</span>
        </div>
      )}
    </div>
  );
}

// Predefined tag options + allow custom
const PRESET_TAGS = ['Renewable', 'Non-Renewable', 'Payment Due', 'Paid', 'VIP', 'At Risk', 'New', 'Churned'];

// Tag color map
const TAG_COLORS = {
  'Renewable':     { bg: '#d1fae5', color: '#065f46' },
  'Non-Renewable': { bg: '#fee2e2', color: '#991b1b' },
  'Payment Due':   { bg: '#fef3c7', color: '#92400e' },
  'Paid':          { bg: '#dbeafe', color: '#1e40af' },
  'VIP':           { bg: '#ede9fe', color: '#5b21b6' },
  'At Risk':       { bg: '#ffedd5', color: '#9a3412' },
  'New':           { bg: '#ecfdf5', color: '#047857' },
  'Churned':       { bg: '#f3f4f6', color: '#374151' },
};
const tagStyle = (t) => TAG_COLORS[t] || { bg: '#fef9c3', color: '#713f12' };

// ── Tag Editor ─────────────────────────────────────────────────────────────
function TagEditor({ tags, onSave }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const ref = React.useRef();
  const btnRef = React.useRef();

  // Close dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openDropdown = (e) => {
    e.stopPropagation();
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    }
    setOpen(o => !o);
  };

  const toggle = (t) => {
    if (tags.includes(t)) onSave(tags.filter(x => x !== t));
    else onSave([...tags, t]);
  };
  const addCustom = () => {
    const t = custom.trim();
    if (t && !tags.includes(t)) { onSave([...tags, t]); setCustom(''); }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      {/* Chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', minWidth: 120 }}>
        {tags.map(t => {
          const s = tagStyle(t);
          return (
            <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: s.bg, color: s.color, borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
              {t}
              <button onClick={e => { e.stopPropagation(); onSave(tags.filter(x => x !== t)); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: s.color, fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          );
        })}
        <button ref={btnRef} onClick={openDropdown}
          style={{ background: 'none', border: '1px dashed #d1d5db', borderRadius: 10, padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
          <FiTag size={10} /> {tags.length === 0 ? 'Add tag' : '+'}
        </button>
      </div>

      {/* Dropdown — position: fixed so it escapes overflow:hidden/visible of any parent */}
      {open && (
        <div ref={ref} style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '8px', minWidth: 190 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', padding: '2px 4px 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quick Tags</div>
          {PRESET_TAGS.map(t => {
            const s = tagStyle(t);
            const active = tags.includes(t);
            return (
              <div key={t} onClick={() => toggle(t)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 6, cursor: 'pointer', background: active ? '#f0fdf4' : 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = active ? '#f0fdf4' : '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = active ? '#f0fdf4' : 'transparent'}>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: active ? '#16a34a' : '#e5e7eb', flexShrink: 0 }} />
                <span style={{ background: s.bg, color: s.color, borderRadius: 8, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{t}</span>
              </div>
            );
          })}
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 6, paddingTop: 6 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <input value={custom} onChange={e => setCustom(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCustom(); }}
                placeholder="Custom tag…"
                style={{ flex: 1, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11 }} />
              <button onClick={addCustom} style={{ padding: '4px 8px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminGHLSubaccounts({ token }) {
  const [activeTab, setActiveTab] = useState('subaccounts');
  const [subaccounts, setSubaccounts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [syncing, setSyncing]     = useState(false);
  const [syncedAt, setSyncedAt]   = useState(null);
  const [expanded, setExpanded]   = useState({});
  const [search, setSearch]       = useState('');
  const [hideZero, setHideZero]   = useState(true);
  const [filterTag, setFilterTag] = useState('');       // filter by adminTag
  const [filterActive, setFilterActive] = useState('all'); // 'all' | 'active' | 'inactive'
  const [selected, setSelected]   = useState(new Set()); // locationIds
  const [exporting, setExporting] = useState(false);
  const [agentEmailEdit, setAgentEmailEdit] = useState({}); // locationId → { open, value, saving }
  const [campaigns, setCampaigns] = useState([]); // [{ campaignId, name }]
  const [syncingContacts, setSyncingContacts] = useState(false);
  const [contactsSyncResult, setContactsSyncResult] = useState(null); // { totalSynced, subaccounts }

  const [oauthStatus, setOauthStatus] = useState(null);
  const syncRef = useRef(false);

  const loadCached = useCallback(async () => {
    try {
      const r = await axios.get('/api/admin/ghl/subaccounts', authH(token));
      setSubaccounts(r.data.subaccounts || []);
      const latest = (r.data.subaccounts||[]).reduce((m,s)=>s.syncedAt>m?s.syncedAt:m,'');
      if (latest) setSyncedAt(latest);
    } catch(e){console.error(e);} finally{setLoading(false);}
  }, [token]);

  const triggerSync = useCallback(async (bg=false) => {
    if (syncRef.current) return;
    syncRef.current=true; setSyncing(true);
    try {
      const r = await axios.post('/api/admin/ghl/subaccounts/sync',{},authH(token));
      setSyncedAt(r.data.syncedAt); await loadCached();
      if(!bg) toast.success(`Synced ${r.data.total} subaccounts`);
    } catch(e){toast.error('Sync failed: '+(e.response?.data?.error||e.message));}
    finally{setSyncing(false);syncRef.current=false;}
  }, [token,loadCached]);

  useEffect(()=>{
    loadCached().then(()=>triggerSync(true));
    // Load OAuth status
    axios.get('/api/admin/crm/oauth/status', authH(token)).then(r=>setOauthStatus(r.data)).catch(()=>{});
    // Load campaigns once for the dropdown — served from server cache, near-instant
    axios.get('/api/admin/campaigns', authH(token))
      .then(r => setCampaigns((r.data.campaigns||[]).filter(c=>c.status!=='archived')))
      .catch(()=>{});
  },[]);

  const updateSub = async (locationId,patch) => {
    try {
      await axios.patch(`/api/admin/ghl/subaccounts/${locationId}`,patch,authH(token));
      setSubaccounts(p=>p.map(s=>s.locationId===locationId?{...s,...patch}:s));
    } catch(e){toast.error('Update failed');}
  };

  const syncAllContacts = async () => {
    if (syncingContacts) return;
    setSyncingContacts(true);
    setContactsSyncResult(null);
    try {
      const r = await axios.post('/api/admin/ghl/contacts/sync-all', {}, authH(token));
      if (r.data.alreadyRunning) {
        toast.info('Sync already running — check progress below');
        setSyncingContacts(false);
        return;
      }
      // Fire-and-forget started — poll status every 4s until done
      toast.info('🔄 Contacts sync started in background…');
      const pollInterval = setInterval(async () => {
        try {
          const s = await axios.get('/api/admin/ghl/contacts/sync-all/status', authH(token));
          const st = s.data;
          setContactsSyncResult(st);
          if (!st.running) {
            clearInterval(pollInterval);
            setSyncingContacts(false);
            if (st.finishedAt) {
              toast.success(`✅ Contacts cached: ${st.totalSynced?.toLocaleString()} contacts across ${st.total} subaccounts (${st.errors} errors)`);
            }
          }
        } catch(e) { clearInterval(pollInterval); setSyncingContacts(false); }
      }, 4000);
    } catch(e) {
      toast.error('Contacts sync failed: ' + (e.response?.data?.error || e.message));
      setSyncingContacts(false);
    }
  };

  const saveAgentEmail = async (locationId) => {
    const val = (agentEmailEdit[locationId]?.value||'').trim().toLowerCase();
    setAgentEmailEdit(p=>({...p,[locationId]:{...p[locationId],saving:true}}));
    try {
      await axios.patch(`/api/admin/ghl/subaccounts/${locationId}`,{ assignedAgentEmail: val },authH(token));
      setSubaccounts(p=>p.map(s=>s.locationId===locationId?{...s,assignedAgentEmail:val}:s));
      setAgentEmailEdit(p=>({...p,[locationId]:{open:false,value:'',saving:false}}));
      toast.success(val ? `Agent assigned: ${val}` : 'Agent assignment cleared');
    } catch(e){
      toast.error('Failed to save');
      setAgentEmailEdit(p=>({...p,[locationId]:{...p[locationId],saving:false}}));
    }
  };



  const allTags = [...new Set(subaccounts.flatMap(s => s.adminTags || []))].sort();

  const filtered = subaccounts.filter(s => {
    if (hideZero && s.contactCount === 0) return false;
    if (filterTag && !(s.adminTags || []).includes(filterTag)) return false;
    if (filterActive === 'active'   && s.isActive === false) return false;
    if (filterActive === 'inactive' && s.isActive !== false) return false;
    const q = search.toLowerCase();
    return !q || s.locationName?.toLowerCase().includes(q) || s.locationId?.toLowerCase().includes(q) || s.address?.toLowerCase().includes(q);
  });

  const allFilteredSelected = filtered.length > 0 && filtered.every(s => selected.has(s.locationId));

  const toggleSelect = (locationId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(locationId) ? next.delete(locationId) : next.add(locationId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelected(prev => { const next = new Set(prev); filtered.forEach(s => next.delete(s.locationId)); return next; });
    } else {
      setSelected(prev => { const next = new Set(prev); filtered.forEach(s => next.add(s.locationId)); return next; });
    }
  };

  const exportContacts = async (format) => {
    const locationIds = [...selected];
    if (!locationIds.length) return;
    setExporting(true);
    try {
      if (format === 'csv') {
        const r = await axios.post('/api/admin/ghl/subaccounts/bulk-export', { locationIds, format: 'csv' }, {
          ...authH(token), responseType: 'blob'
        });
        const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
        const a = document.createElement('a'); a.href = url;
        a.download = `ghl-contacts-${Date.now()}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('CSV downloaded');
      } else {
        // JSON → generate PDF in browser using print
        const r = await axios.post('/api/admin/ghl/subaccounts/bulk-export', { locationIds, format: 'json' }, authH(token));
        const contacts = r.data.contacts || [];
        const errors   = r.data.errors   || [];
        // Build printable HTML and open in new tab
        const rows = contacts.map((c, i) => `
          <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
            <td>${i+1}</td>
            <td>${c.subaccount}</td>
            <td>${c.firstName} ${c.lastName}</td>
            <td>${c.email}</td>
            <td>${c.phone}</td>
            <td>${c.tags}</td>
            <td>${c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}</td>
          </tr>`).join('');
        const errNote = errors.length ? `<p style="color:#dc2626;font-size:12px">⚠️ ${errors.length} subaccount(s) could not be fetched</p>` : '';
        const html = `<!DOCTYPE html><html><head><title>GHL Contacts Export</title>
          <style>body{font-family:sans-serif;padding:24px;font-size:12px}
          h2{margin-bottom:4px}p{margin:2px 0 12px;color:#6b7280}
          table{width:100%;border-collapse:collapse}
          th{background:#1a56db;color:#fff;padding:6px 10px;text-align:left;font-size:11px}
          td{padding:5px 10px;border-bottom:1px solid #e5e7eb}
          @media print{button{display:none}}</style></head>
          <body>
          <h2>GHL Contacts Export</h2>
          <p>${contacts.length.toLocaleString()} contacts · ${locationIds.length} subaccount(s) · ${new Date().toLocaleString()}</p>
          ${errNote}
          <button onclick="window.print()" style="margin-bottom:12px;padding:6px 16px;background:#1a56db;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px">🖨 Print / Save as PDF</button>
          <table><thead><tr>
            <th>#</th><th>Subaccount</th><th>Name</th><th>Email</th><th>Phone</th><th>Tags</th><th>Created</th>
          </tr></thead><tbody>${rows}</tbody></table>
          </body></html>`;
        const win = window.open('', '_blank');
        win.document.write(html);
        win.document.close();
        toast.success(`PDF ready — ${contacts.length} contacts`);
      }
    } catch(e) {
      toast.error('Export failed: ' + (e.response?.data?.error || e.message));
    } finally { setExporting(false); }
  };

  return (
    <div style={{maxWidth:1400}}>
      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div style={{display:'flex',gap:0,borderBottom:'2px solid #e5e7eb',marginBottom:0,paddingLeft:24}}>
        {[
          { key: 'subaccounts', label: '🏢 Subaccounts' },
          { key: 'templates',   label: '✉️ Message Templates' },
        { key: 'blast',       label: '📢 Blast' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '12px 22px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? '#1a56db' : '#6b7280',
              borderBottom: activeTab === tab.key ? '2px solid #1a56db' : '2px solid transparent',
              marginBottom: -2, transition: 'all 0.15s',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'templates' && <AdminMessageTemplates token={token} />}
      {activeTab === 'blast'     && <AdminBlast token={token} />}
      {activeTab === 'subaccounts' && <div style={{padding:24}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,flexWrap:'wrap',gap:12}}>
        <div>
          <h2 style={{margin:0,fontSize:22,fontWeight:700}}>GHL Subaccounts</h2>
          <p style={{margin:'4px 0 0',fontSize:13,color:'#6b7280'}}>
            {syncing?'🔄 Syncing from GHL…':syncedAt?`Last synced: ${new Date(syncedAt).toLocaleString()}`:'Not yet synced'}
          </p>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, ID, address…"
            style={{padding:'8px 12px',border:'1px solid #d1d5db',borderRadius:7,fontSize:13,width:280}}/>
          <button onClick={()=>setHideZero(p=>!p)}
            title={hideZero ? 'Currently hiding 0-contact subaccounts — click to show them' : 'Currently showing all subaccounts — click to hide 0-contact ones'}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 12px',border:'1px solid #d1d5db',borderRadius:7,fontSize:12,fontWeight:500,cursor:'pointer',background:hideZero?'#fef3c7':'#f9fafb',color:hideZero?'#92400e':'#6b7280',whiteSpace:'nowrap'}}>
            {hideZero ? '🚫 Hide 0 contacts' : '👁 Show 0 contacts'}
          </button>
          <button onClick={()=>triggerSync(false)} disabled={syncing}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:syncing?'#9ca3af':'#1a56db',color:'#fff',border:'none',borderRadius:7,fontSize:13,fontWeight:600,cursor:syncing?'default':'pointer'}}>
            <FiRefreshCw size={14} style={{animation:syncing?'spin 1s linear infinite':'none'}}/>
            {syncing?'Syncing…':'Sync Now'}
          </button>

          {/* Cache all contacts into GHL-Contacts table */}
          <button onClick={syncAllContacts} disabled={syncingContacts}
            title="Fetch all contacts from every subaccount and store them in our GHL-Contacts cache table (used by check-lead API)"
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',
              background:syncingContacts?'#9ca3af':'#7c3aed',color:'#fff',border:'none',
              borderRadius:7,fontSize:13,fontWeight:600,cursor:syncingContacts?'default':'pointer'}}>
            <FiRefreshCw size={14} style={{animation:syncingContacts?'spin 1s linear infinite':'none'}}/>
            {syncingContacts?'Caching Contacts…':'Cache All Contacts'}
          </button>
        </div>
      </div>

      {/* Contacts sync result banner */}
      {contactsSyncResult && (
        <div style={{background: contactsSyncResult.running ? '#eff6ff' : '#f0fdf4',
          border: `1px solid ${contactsSyncResult.running ? '#93c5fd' : '#86efac'}`,
          borderRadius:8, padding:'10px 16px', marginBottom:12, fontSize:13,
          display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          {contactsSyncResult.running ? (
            <>
              <span style={{color:'#1d4ed8',fontWeight:600}}>🔄 Syncing contacts…</span>
              <span style={{color:'#374151'}}>
                {contactsSyncResult.done} / {contactsSyncResult.total} subaccounts done
                — {contactsSyncResult.totalSynced?.toLocaleString()} contacts stored so far
              </span>
              {contactsSyncResult.current && (
                <span style={{color:'#6b7280',fontSize:11}}>Current: {contactsSyncResult.current}</span>
              )}
              {/* Progress bar */}
              <div style={{width:'100%',background:'#dbeafe',borderRadius:4,height:6,marginTop:4}}>
                <div style={{width:`${contactsSyncResult.total ? Math.round(contactsSyncResult.done/contactsSyncResult.total*100) : 0}%`,
                  background:'#1d4ed8',borderRadius:4,height:6,transition:'width 0.4s'}}/>
              </div>
            </>
          ) : (
            <>
              <span style={{color:'#15803d',fontWeight:600}}>✅ Contacts cached successfully</span>
              <span style={{color:'#6b7280'}}>
                {contactsSyncResult.totalSynced?.toLocaleString()} contacts across {contactsSyncResult.total} subaccounts
                {contactsSyncResult.errors > 0 && ` (${contactsSyncResult.errors} errors)`}
                {' '}— stored with agent email, campaign type &amp; master status
              </span>
            </>
          )}
          <button onClick={()=>setContactsSyncResult(null)}
            style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'#9ca3af',fontSize:16}}>✕</button>
        </div>
      )}

      {/* GHL OAuth status banner */}
      {oauthStatus && (
        <div style={{background: oauthStatus.connected ? '#f0fdf4' : '#fffbeb', border: `1px solid ${oauthStatus.connected ? '#86efac' : '#fde68a'}`, borderRadius:8, padding:'10px 16px', marginBottom:12, fontSize:13, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          {oauthStatus.connected ? (
            <>
              <span style={{color:'#15803d', fontWeight:600}}>✅ GHL OAuth Connected</span>
              <span style={{color:'#6b7280'}}>Contacts load automatically for all subaccounts · companyId: {oauthStatus.companyId} · connected {oauthStatus.connectedAt ? new Date(oauthStatus.connectedAt).toLocaleDateString() : ''}</span>
            </>
          ) : (
            <>
              <span style={{color:'#92400e', fontWeight:600}}>⚠️ GHL OAuth Not Connected</span>
              <span style={{color:'#6b7280'}}>{oauthStatus.reason}</span>
              {!oauthStatus.reason?.includes('not set') && (
                <a href="/api/admin/crm/oauth/connect" target="_blank"
                  style={{marginLeft:'auto', padding:'4px 12px', background:'#1a56db', color:'#fff', borderRadius:6, fontSize:12, fontWeight:600, textDecoration:'none'}}>
                  🔗 Connect Now
                </a>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Filter bar ─────────────────────────────────────────────────── */}
      {!loading && (
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,flexWrap:'wrap'}}>
          {/* Tag filter */}
          <select value={filterTag} onChange={e=>setFilterTag(e.target.value)}
            style={{padding:'6px 10px',border:'1px solid #d1d5db',borderRadius:7,fontSize:12,color:'#374151'}}>
            <option value="">All Tags</option>
            {allTags.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          {/* Active/inactive filter */}
          <select value={filterActive} onChange={e=>setFilterActive(e.target.value)}
            style={{padding:'6px 10px',border:'1px solid #d1d5db',borderRadius:7,fontSize:12,color:'#374151'}}>
            <option value="all">All Status</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          {(filterTag||filterActive!=='all') && (
            <button onClick={()=>{setFilterTag('');setFilterActive('all');}}
              style={{fontSize:12,padding:'6px 10px',border:'1px solid #fca5a5',borderRadius:7,background:'#fee2e2',color:'#dc2626',cursor:'pointer'}}>
              ✕ Clear filters
            </button>
          )}
          <span style={{marginLeft:'auto',fontSize:12,color:'#6b7280'}}>
            Showing <strong>{filtered.length}</strong> of <strong>{subaccounts.length}</strong>
          </span>
        </div>
      )}

      {/* ── Stats bar ──────────────────────────────────────────────────── */}
      {!loading&&(
        <div style={{background:'#eff6ff',border:'1px solid #dbeafe',borderRadius:8,padding:'10px 16px',marginBottom:12,fontSize:13,color:'#1e40af'}}>
          <strong>{filtered.length}</strong> subaccount{filtered.length!==1?'s':''}{search&&<> matching "<strong>{search}</strong>"</>}
          {hideZero && subaccounts.filter(s=>s.contactCount===0).length > 0 &&
            <span style={{color:'#9ca3af'}}> (<strong>{subaccounts.filter(s=>s.contactCount===0).length}</strong> zero-contact hidden)</span>
          }{' · '}
          <strong>{subaccounts.filter(s=>s.isActive!==false).length}</strong> active{' · '}
          <strong>{subaccounts.filter(s=>s.isActive===false).length}</strong> inactive{' · '}
          {(() => {
            const withCount = subaccounts.filter(s => s.contactCount != null);
            const total = withCount.reduce((sum, s) => sum + s.contactCount, 0);
            if (withCount.length === 0) return <span style={{color:'#9ca3af'}}>— total contacts</span>;
            return <>
              <strong style={{color:'#15803d'}}>{total.toLocaleString()}</strong>
              <span style={{color:'#1e40af'}}> total contacts</span>
            </>;
          })()}
        </div>
      )}

      {/* ── Bulk action bar — appears when rows are selected ───────────── */}
      {selected.size > 0 && (
        <div style={{display:'flex',alignItems:'center',gap:10,background:'#1e40af',borderRadius:8,padding:'10px 16px',marginBottom:12,flexWrap:'wrap'}}>
          <span style={{color:'#fff',fontWeight:600,fontSize:13}}>
            {selected.size} subaccount{selected.size!==1?'s':''} selected
          </span>
          <button onClick={()=>setSelected(new Set())}
            style={{fontSize:12,padding:'4px 10px',background:'rgba(255,255,255,0.15)',color:'#fff',border:'1px solid rgba(255,255,255,0.3)',borderRadius:6,cursor:'pointer'}}>
            ✕ Deselect all
          </button>
          <div style={{marginLeft:'auto',display:'flex',gap:8}}>
            <button onClick={()=>exportContacts('csv')} disabled={exporting}
              style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',background:'#16a34a',color:'#fff',border:'none',borderRadius:7,fontSize:13,fontWeight:600,cursor:exporting?'default':'pointer',opacity:exporting?0.7:1}}>
              {exporting ? '⏳ Exporting…' : '⬇ Download CSV'}
            </button>
            <button onClick={()=>exportContacts('pdf')} disabled={exporting}
              style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',background:'#dc2626',color:'#fff',border:'none',borderRadius:7,fontSize:13,fontWeight:600,cursor:exporting?'default':'pointer',opacity:exporting?0.7:1}}>
              {exporting ? '⏳ Exporting…' : '🖨 Download PDF'}
            </button>
          </div>
        </div>
      )}

      {loading?<div style={{textAlign:'center',padding:60,color:'#9ca3af'}}>Loading…</div>
      :filtered.length===0?<div style={{textAlign:'center',padding:60,color:'#9ca3af'}}>{search?'No subaccounts match':'No subaccounts yet — click Sync Now'}</div>
      :(
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {/* Select-all row */}
          <div style={{display:'flex',alignItems:'center',gap:8,padding:'4px 18px'}}>
            <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll}
              style={{width:15,height:15,cursor:'pointer',accentColor:'#1a56db'}}/>
            <span style={{fontSize:12,color:'#6b7280',cursor:'pointer'}} onClick={toggleSelectAll}>
              {allFilteredSelected ? 'Deselect all' : `Select all ${filtered.length}`}
            </span>
          </div>
          {filtered.map(s=>(
            <div key={s.locationId} style={{background:'#fff',border:`1px solid ${selected.has(s.locationId)?'#1a56db':s.isActive===false?'#fee2e2':s.ghlStatus==='inactive'?'#fed7aa':'#e5e7eb'}`,borderRadius:10,overflow:'visible',opacity:s.isActive===false?0.75:1,boxShadow:selected.has(s.locationId)?'0 0 0 2px #bfdbfe':undefined}}>
              <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px 18px',cursor:'pointer'}} onClick={()=>setExpanded(p=>({...p,[s.locationId]:!p[s.locationId]}))}>
                <div onClick={e=>e.stopPropagation()} style={{flexShrink:0}}>
                  <input type="checkbox" checked={selected.has(s.locationId)} onChange={()=>toggleSelect(s.locationId)}
                    style={{width:15,height:15,cursor:'pointer',accentColor:'#1a56db'}}/>
                </div>
                <span style={{color:'#9ca3af',flexShrink:0}}>{expanded[s.locationId]?<FiChevronDown size={16}/>:<FiChevronRight size={16}/>}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:15,color:'#111827',display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    {s.locationName||s.locationId}
                    {/* GHL status badge */}
                    {s.ghlStatus
                      ? <span style={{fontSize:11,borderRadius:10,padding:'1px 8px',fontWeight:600,
                          background: s.ghlStatus==='active'?'#d1fae5': s.ghlStatus==='inactive'?'#fee2e2':'#f3f4f6',
                          color:      s.ghlStatus==='active'?'#065f46': s.ghlStatus==='inactive'?'#b91c1c':'#374151'}}>
                          GHL: {s.ghlStatus}
                        </span>
                      : <span style={{fontSize:11,background:'#f3f4f6',color:'#9ca3af',borderRadius:10,padding:'1px 8px'}}>GHL: unknown</span>
                    }
                  </div>
                  <div style={{fontSize:12,color:'#9ca3af',marginTop:2}}>
                    ID: {s.locationId}{s.address&&<> · {s.address}</>}{s.timezone&&<> · {s.timezone}</>}
                    {s.contactCount != null
                      ? <span style={{marginLeft:8,background:'#f0fdf4',color:'#15803d',borderRadius:10,padding:'1px 8px',fontSize:11,fontWeight:600}}>
                          {s.contactCount.toLocaleString()} contacts
                        </span>
                      : <span style={{marginLeft:8,background:'#f3f4f6',color:'#9ca3af',borderRadius:10,padding:'1px 8px',fontSize:11}}>
                          — contacts
                        </span>
                    }
                  </div>
                </div>
                <div onClick={e=>e.stopPropagation()} style={{minWidth:160}}>
                  <TagEditor tags={s.adminTags||[]} onSave={tags=>updateSub(s.locationId,{adminTags:tags})}/>
                </div>

                {/* Master status toggle (isActive) */}
                <div onClick={e=>e.stopPropagation()} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3,flexShrink:0}}>
                  <button onClick={()=>updateSub(s.locationId,{isActive:s.isActive===false})}
                    title={s.isActive!==false?'Master: Active — click to deactivate':'Master: Inactive — click to activate'}
                    style={{background:'none',border:'none',cursor:'pointer',padding:0,color:s.isActive!==false?'#16a34a':'#d1d5db'}}>
                    {s.isActive!==false?<FiToggleRight size={22}/>:<FiToggleLeft size={22}/>}
                  </button>
                  <span style={{fontSize:10,color:'#9ca3af',whiteSpace:'nowrap'}}>{s.isActive!==false?'Master: On':'Master: Off'}</span>
                </div>

                {/* Assigned agent email */}
                <div onClick={e=>e.stopPropagation()} style={{flexShrink:0}}>
                  {agentEmailEdit[s.locationId]?.open ? (
                    <div style={{display:'flex',alignItems:'center',gap:4}}>
                      <input
                        autoFocus
                        placeholder="agent@email.com"
                        value={agentEmailEdit[s.locationId]?.value||''}
                        onChange={e=>setAgentEmailEdit(p=>({...p,[s.locationId]:{...p[s.locationId],value:e.target.value}}))}
                        onKeyDown={e=>{if(e.key==='Enter')saveAgentEmail(s.locationId);if(e.key==='Escape')setAgentEmailEdit(p=>({...p,[s.locationId]:{open:false}}));}}
                        style={{fontSize:11,padding:'3px 6px',border:'1px solid #d1d5db',borderRadius:5,width:190,fontFamily:'monospace'}}
                      />
                      <button onClick={()=>saveAgentEmail(s.locationId)} disabled={agentEmailEdit[s.locationId]?.saving}
                        style={{fontSize:11,padding:'3px 8px',background:'#16a34a',color:'#fff',border:'none',borderRadius:5,cursor:'pointer'}}>
                        {agentEmailEdit[s.locationId]?.saving?'…':'Save'}
                      </button>
                      <button onClick={()=>setAgentEmailEdit(p=>({...p,[s.locationId]:{open:false}}))}
                        style={{fontSize:11,padding:'3px 6px',background:'none',border:'1px solid #d1d5db',borderRadius:5,cursor:'pointer'}}>✕</button>
                    </div>
                  ) : (
                    <button
                      title={s.assignedAgentEmail ? `Assigned to ${s.assignedAgentEmail} — click to change` : 'Assign an agent to this subaccount'}
                      onClick={()=>setAgentEmailEdit(p=>({...p,[s.locationId]:{open:true,value:s.assignedAgentEmail||'',saving:false}}))}
                      style={{fontSize:11,padding:'3px 8px',background:s.assignedAgentEmail?'#f0fdf4':'#f9fafb',border:`1px solid ${s.assignedAgentEmail?'#86efac':'#d1d5db'}`,borderRadius:6,cursor:'pointer',color:s.assignedAgentEmail?'#15803d':'#6b7280',display:'flex',alignItems:'center',gap:4,maxWidth:170,overflow:'hidden'}}>
                      👤 <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.assignedAgentEmail||'Assign Agent'}</span>
                    </button>
                  )}
                </div>

                {/* Assigned campaign dropdown */}
                <div onClick={e=>e.stopPropagation()} style={{flexShrink:0}}>
                  <select
                    value={s.assignedCampaign||''}
                    onChange={e=>updateSub(s.locationId,{assignedCampaign:e.target.value||null})}
                    title={s.assignedCampaign?`Campaign: ${s.assignedCampaign}`:'Assign a campaign'}
                    style={{
                      fontSize:11,padding:'3px 7px',
                      border:`1px solid ${s.assignedCampaign?'#93c5fd':'#d1d5db'}`,
                      borderRadius:6,cursor:'pointer',
                      background:s.assignedCampaign?'#eff6ff':'#f9fafb',
                      color:s.assignedCampaign?'#1d4ed8':'#6b7280',
                      maxWidth:130,
                    }}>
                    <option value="">🏷 Campaign</option>
                    {campaigns.map(c=>(
                      <option key={c.campaignId} value={c.campaignId}>{c.name||c.campaignId}</option>
                    ))}
                  </select>
                </div>

                <div style={{fontSize:11,color:'#9ca3af',textAlign:'right',flexShrink:0}}>{s.syncedAt?new Date(s.syncedAt).toLocaleDateString():'—'}</div>
              </div>
              {expanded[s.locationId]&&<ContactsPanel locationId={s.locationId} token={token}/>}
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
      </div>}
    </div>
  );
}
